/**
 * Credit Card Form Stimulus Controller
 * Converted from credit_card_form.vue
 * Handles Stripe Elements integration and form validation
 */

import { Controller } from '@hotwired/stimulus'
import StripeJS from '../shared/stripe_form/stripe.js'

export default class extends Controller {
  static targets = ['termsError', 'submitButton']

  static values = {
    address: String,
    address2: String,
    city: String,
    clientSecret: String,
    country: String,
    name: String,
    phone: String,
    state: String,
    zipcode: String,
    includeChecks: Boolean,
    termsErrorText: String
  }

  connect() {
    this.acceptedTerms = false
    this.loading = false
    this.displayError = null
    this.buildStripeForm()
  }

  disconnect() {
    StripeJS.removeElements()
  }

  async buildStripeForm() {
    try {
      // Import CartApi dynamically to check for discounts
      const { default: CartApi } = await import('../shared/cart/cart_api')
      const discount = await CartApi.getDiscounts()
      if (discount && discount.discount_type === 'amex_only') {
        StripeJS.generateForm(true)
      } else {
        StripeJS.generateForm()
      }
    } catch (error) {
      console.error('Error building Stripe form:', error)
      StripeJS.generateForm()
    }
  }

  updateTerms(event) {
    this.acceptedTerms = event.target.checked
    if (!this.acceptedTerms) {
      this.displayError = this.termsErrorTextValue
      this.showTermsError()
    } else {
      this.displayError = null
      this.hideTermsError()
    }
  }

  updateF1Consent(event) {
    this.dispatch('update-f1', { detail: { consent: event.target.checked } })
  }

  async submitCard(event) {
    event.preventDefault()

    if (this.shouldDisableSubmit()) {
      this.displayError = this.termsErrorTextValue
      this.showTermsError()
      return
    }

    this.setLoading(true)
    try {
      const cardId = this.clientSecretValue
        ? await StripeJS.cardIDToBeChargedLater(this.clientSecretValue, this.billingDetails)
        : await StripeJS.cardIDToBeChargedImmediately(this.billingDetails)
      if (cardId === 'error') {
        this.setLoading(false)
      } else {
        await this.attachPaymentMethodToCustomer(cardId)
      }
    } catch (error) {
      console.error('Error generating card ID:', error)
      this.setLoading(false)
    }
  }

  async attachPaymentMethodToCustomer(paymentMethodId) {
    const locale = document.documentElement.lang || 'en'
    const url = `/${locale}/oa/payment_methods.json`
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCsrfToken()
        },
        body: JSON.stringify({ id: paymentMethodId })
      })
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to attach payment method: ${response.status}. ${errorText}`)
      }
      const card = await response.json()
      if (card.id) {
        this.dispatch('card-added', {
          detail: {
            id: card.id,
            card: card,
            stripe: StripeJS
          },
          bubbles: true
        })
        this.setLoading(false)
      } else {
        alert('Failed to add payment method. Please try again or contact support.')
        this.setLoading(false)
      }
    } catch (error) {
      console.error('Error attaching payment method:', error)
      alert(`Error: ${error.message}`)
      this.setLoading(false)
    }
  }

  getCsrfToken() {
    const token = document.querySelector('meta[name="csrf-token"]')
    return token ? token.content : ''
  }

  get billingDetails() {
    return {
      address: {
        city: this.cityValue || '',
        country: this.countryValue || '',
        line1: this.addressValue || '',
        line2: this.address2Value || '',
        postal_code: this.zipcodeValue || '',
        state: this.stateValue || ''
      },
      name: this.nameValue || '',
      phone: this.phoneValue || ''
    }
  }

  shouldDisableSubmit() {
    return this.includeChecksValue && !this.acceptedTerms
  }

  showTermsError() {
    if (this.hasTermsErrorTarget) {
      this.termsErrorTarget.textContent = this.displayError
      this.termsErrorTarget.style.display = 'block'
    }
  }

  hideTermsError() {
    if (this.hasTermsErrorTarget) {
      this.termsErrorTarget.style.display = 'none'
    }
  }

  setLoading(isLoading) {
    this.loading = isLoading

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = isLoading
    }

    if (isLoading) {
      this.element.classList.add('loading-card')
    } else {
      this.element.classList.remove('loading-card')
    }
  }
}
